import { reducerWithInitialState } from "typescript-fsa-reducers";

import withImmer from "../../lib/withImmer";
import {
  bootstrapCompanyMembers,
  loadCompanyMembers,
  bootstrapCompanyShow,
  loadCompanyShow,
  draftMembershipsChanged,
  saveDraftCompanyMemberships,
  draftReset,
} from "../actions";
import {
  CompanyMembersResponse,
  CompanyShowResponse,
  CompanyMembersResponseModels,
  FeaturedCompanymembership,
} from "../api";
import { sectionSelector, featuredMembershipsSelector } from "../selectors";
import { EntityValidationResultMap, EntityValidationMapError } from "../../lib/entities/validation";

export type CompanyMembership = CompanyMembersResponseModels["CompanyMembership"];
export type CompanyMembershipSection = CompanyMembersResponseModels["CompanyMembershipSection"];
export type FeaturedCompanymembership = FeaturedCompanymembership;

export interface DenormalizedSection {
  section?: CompanyMembershipSection;
  memberships: CompanyMembership[];
}

export interface MembersState {
  memberships: CompanyMembership[];
  sections: CompanyMembershipSection[];
  isLoaded: boolean;
  draftMembershipSections: DenormalizedSection[];
  draftFeaturedMemberships: FeaturedCompanymembership[];
  draftMembershipSectionsErrors: EntityValidationResultMap<CompanyMembershipSection>;
  isDraftMembershipsDirty: boolean;
}

const InitialState: MembersState = {
  sections: [],
  memberships: [],
  isLoaded: false,
  draftMembershipSections: [],
  draftFeaturedMemberships: [],
  draftMembershipSectionsErrors: {},
  isDraftMembershipsDirty: false,
};

const retrieveStates = (state: MembersState, result: CompanyMembersResponse) => {
  Object.assign(state, {
    sections: result.sections,
    memberships: result.memberships,
    isLoaded: true,
    draftMembershipSections: sectionSelector(result.memberships, result.sections),
    draftFeaturedMemberships: featuredMembershipsSelector(result.memberships),
    draftMembershipSectionsErrors: {},
    isDraftMembershipsDirty: false,
  });
  return state;
};

function initStateFromBootstrapShow(state: MembersState, result: CompanyShowResponse) {
  state.draftFeaturedMemberships = featuredMembershipsSelector(result.featured_memberships);
  return state;
}

const membersReducer = withImmer(reducerWithInitialState<MembersState>(InitialState))
  .case(bootstrapCompanyMembers, (state, response) => retrieveStates(state, response))
  .case(loadCompanyMembers.done, (state, { result: actionRes }) => retrieveStates(state, actionRes))
  .case(bootstrapCompanyShow, initStateFromBootstrapShow)
  .case(loadCompanyShow.done, (state, { result: actionRes }) => initStateFromBootstrapShow(state, actionRes))
  .case(draftReset, state => {
    Object.assign(state, {
      draftMembershipSections: sectionSelector(state.memberships, state.sections),
      draftFeaturedMemberships: featuredMembershipsSelector(state.memberships),
      draftMembershipSectionsErrors: {},
      isDraftMembershipsDirty: false,
    });
    return state;
  })
  .case(draftMembershipsChanged, (state, result) => {
    Object.assign(state, {
      draftMembershipSections: result.membershipSections,
      draftFeaturedMemberships: result.featuredMemberships,
      isDraftMembershipsDirty: true,
    });
    return state;
  })
  .case(saveDraftCompanyMemberships.done, (state, { result: { sections, updatedMemberships } }) => {
    state.memberships.forEach(membership => {
      const newMembership = updatedMemberships.find(m => m.id === membership.id);
      if (newMembership) {
        membership.display_order = newMembership.display_order;
        membership.featured_display_order = newMembership.featured_display_order;
        membership.section = newMembership.section;
      }
    });
    Object.assign(state, {
      sections,
      draftMembershipSections: sectionSelector(state.memberships, sections),
      draftFeaturedMemberships: featuredMembershipsSelector(state.memberships),
      draftMembershipSectionsErrors: {},
      isDraftMembershipsDirty: false,
    });
    return state;
  })
  .case(saveDraftCompanyMemberships.failed, (state, { error }) => {
    if (error instanceof EntityValidationMapError) {
      state.draftMembershipSectionsErrors = error.getResult();
    }
    return state;
  });

export default membersReducer;



// WEBPACK FOOTER //
// ./frontend/src/companies/reducers/members.ts